import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import css from './SemiFinishedCreateModal.module.css';
import { SemiFinishedLeftBlockModal } from "../SemiFinishedLeftBlockModal/SemiFinishedLeftBlockModal";
import { SemiFinishedRightBlockModal } from "../SemiFinishedRightBlockModal/SemiFinishedRightBlockModal";
import { SemiFinishedAddProductModal } from "../SemiFinishedAddProductModal/SemiFinishedAddProductModal";
import { SemiFinishedAddSemiFinishedModal } from "../SemiFinishedAddSemiFinishedModal/SemiFinishedAddSemiFinishedModal";
import {
    arrayFormattedProducts,
    arrayFormattedSemiFinished,
    semiFinishedActions,
    semiFinishedProductsActions,
    semiFinishedSemiFinishedActions
} from "../../../../store/slices/semiFinished-slice";
import { semiFinishedService } from "../../../../services/semiFinished-services";

const NUTRITION_OVERRIDE_DEFAULTS = {
  energyOverridden: false,
  proteinsOverridden: false,
  fatsOverridden: false,
  carbohydratesOverridden: false,
  costOverridden: false,
};

const SemiFinishedCreateModal = ({ setIsOpen }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const methods = useForm({ defaultValues: NUTRITION_OVERRIDE_DEFAULTS });
    const { setValue, watch } = methods;

    const energyOverridden = watch("energyOverridden");
    const proteinsOverridden = watch("proteinsOverridden");
    const fatsOverridden = watch("fatsOverridden");
    const carbohydratesOverridden = watch("carbohydratesOverridden");
    const costOverridden = watch("costOverridden");

    const [selectedCategory, setSelectedCategory] = useState("");
    const [uploadedImage, setUploadedImage] = useState(null);
    const [semiFinishedProductAddOpen, setSemiFinishedProductAddOpen] = useState(false);
    const [semiFinishedSemiFinishedAddOpen, setSemiFinishedSemiFinishedAddOpen] = useState(false);

    // Clear arrays when modal opens to ensure fresh state
    useEffect(() => {
        dispatch(semiFinishedProductsActions.clearArray());
        dispatch(semiFinishedSemiFinishedActions.clearArray());
    }, [dispatch]);

    const {
        semiFinishedProductsWeight,
        semiFinishedProductsWeightNetto,
        semiFinishedProductsCost,
        semiFinishedProductEnergy,
        semiFinishedProductsFats,
        semiFinishedProductsProteins,
        semiFinishedProductsCarbohydrates
    } = useSelector((store) => store.semiFinishedProducts);

    const {
        semiFinishedSemiFinishedWeight,
        semiFinishedSemiFinishedWeightNetto,
        semiFinishedSemiFinishedCost,
        semiFinishedSemiFinishedEnergy,
        semiFinishedSemiFinishedFats,
        semiFinishedSemiFinishedProteins,
        semiFinishedSemiFinishedCarbohydrates
    } = useSelector((store) => store.semiFinishedSemiFinished);

    const formattedProducts = useSelector(arrayFormattedProducts);
    const formattedSemiFinished = useSelector(arrayFormattedSemiFinished);

    useEffect(() => {
        const currentWeight = watch("weight");
        if (!currentWeight) {
            setValue("weight", semiFinishedProductsWeight + semiFinishedSemiFinishedWeight);
        }
        setValue("weightNetto", semiFinishedProductsWeightNetto + semiFinishedSemiFinishedWeightNetto);
        if (!costOverridden) {
            setValue("Cost", semiFinishedProductsCost + semiFinishedSemiFinishedCost);
        }
        if (!fatsOverridden) {
            setValue("Fats", semiFinishedProductsFats + semiFinishedSemiFinishedFats);
        }
        if (!proteinsOverridden) {
            setValue("Proteins", semiFinishedProductsProteins + semiFinishedSemiFinishedProteins);
        }
        if (!carbohydratesOverridden) {
            setValue("Carbohydrates", semiFinishedProductsCarbohydrates + semiFinishedSemiFinishedCarbohydrates);
        }
        if (!energyOverridden) {
            setValue("Energy", semiFinishedProductEnergy + semiFinishedSemiFinishedEnergy);
        }
    }, [
        semiFinishedProductsWeight,
        semiFinishedSemiFinishedWeight,
        semiFinishedProductsWeightNetto,
        semiFinishedSemiFinishedWeightNetto,
        semiFinishedProductsCost,
        semiFinishedSemiFinishedCost,
        semiFinishedProductsFats,
        semiFinishedSemiFinishedFats,
        semiFinishedProductsProteins,
        semiFinishedSemiFinishedProteins,
        semiFinishedProductsCarbohydrates,
        semiFinishedSemiFinishedCarbohydrates,
        semiFinishedProductEnergy,
        semiFinishedSemiFinishedEnergy,
        setValue,
        watch,
        energyOverridden,
        proteinsOverridden,
        fatsOverridden,
        carbohydratesOverridden,
        costOverridden,
    ]);

    const handleCategoryChange = (e) => {
        const value = e.target.value;
        setSelectedCategory(value);
        setValue("categoryId", value);
    };

    const onSubmit = async (data) => {
        try {
            const totalWeight = semiFinishedProductsWeight + semiFinishedSemiFinishedWeight;

            const formData = new FormData();
            if (uploadedImage) {
                formData.append("file", uploadedImage);
            }

            formData.append("name", JSON.stringify({
                en: data.nameEn,
                ru: data.nameRu,
                ge: data.nameKa,
                ar: data.nameAr,
            }));

            formData.append("description", JSON.stringify({
                en: data.descriptionEn,
                ru: data.descriptionRu,
                ge: data.descriptionKa,
                ar: data.descriptionAr,
            }));

            formData.append("recipe", JSON.stringify({
                en: data.recipeEn,
                ru: data.recipeRu,
                ge: data.recipeGe,
                ar: data.recipeAr,
            }));

            formData.append("img", 'placeholder'); // або id, якщо вже є
            formData.append("cost", Number(data.Cost) || 0);
            formData.append("price", parseFloat(data.price) || 0);
            formData.append("weight", data.weight ? Number(data.weight) : totalWeight);
            formData.append("weightNetto", Number(data.weightNetto) || 0);
            formData.append("categoryId", data.categoryId || "");
            formData.append("products", JSON.stringify(formattedProducts));
            formData.append("semiFinisheds", JSON.stringify(formattedSemiFinished));

            if (data.energyOverridden) {
                formData.append("energy", data.Energy);
            }
            if (data.proteinsOverridden) {
                formData.append("proteins", data.Proteins);
            }
            if (data.fatsOverridden) {
                formData.append("fats", data.Fats);
            }
            if (data.carbohydratesOverridden) {
                formData.append("carbohydrates", data.Carbohydrates);
            }

            formData.append("energyOverridden", data.energyOverridden ? "true" : "false");
            formData.append("proteinsOverridden", data.proteinsOverridden ? "true" : "false");
            formData.append("fatsOverridden", data.fatsOverridden ? "true" : "false");
            formData.append("carbohydratesOverridden", data.carbohydratesOverridden ? "true" : "false");
            formData.append("costOverridden", data.costOverridden ? "true" : "false");

            await semiFinishedService.createSemiFinish(formData);
            dispatch(semiFinishedActions.changeTrigger());
            toast.success(t("New semiFinish was created!"));
            setIsOpen(false);
        } catch (error) {
            console.error(error);
            toast.error(t("modal-toast-creating-error"));
        }
    };

    return (
        <section className={css.overlay}>
            <div className={css.modal}>
                <div className={css.header}>
                    <div className={css.title}>{t("modal-new-semi-finished")}</div>
                    <div className={css.closeButton} onClick={() => setIsOpen(false)}>×</div>
                </div>

                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)} className={css.form}>
                        <div className={css.infoBlock}>
                            <div className={css.leftBlock}>
                                <SemiFinishedLeftBlockModal
                                    setSemiFinishedProductAddOpen={setSemiFinishedProductAddOpen}
                                    setSemiFinishedSemiFinishedAddOpen={setSemiFinishedSemiFinishedAddOpen}
                                    setUploadedImage={setUploadedImage}
                                />
                            </div>

                            <div className={css.rightBlock}>
                                <SemiFinishedRightBlockModal
                                    selectedCategory={selectedCategory}
                                    setSelectedCategory={setSelectedCategory}
                                    handleCategoryChange={handleCategoryChange}
                                />
                            </div>
                        </div>

                        <div className={css.buttonsBlock}>
                            <button type="button" className={css.cancelButton} onClick={() => setIsOpen(false)}>
                                {t("modal-cancel")}
                            </button>
                            <button type="submit" className={css.submitButton}>OK</button>
                        </div>
                    </form>
                </FormProvider>
            </div>

            {semiFinishedProductAddOpen && (
                <SemiFinishedAddProductModal setSemiFinishedProductAddOpen={setSemiFinishedProductAddOpen} />
            )}

            {semiFinishedSemiFinishedAddOpen && (
                <SemiFinishedAddSemiFinishedModal setSemiFinishedSemiFinishedAddOpen={setSemiFinishedSemiFinishedAddOpen} />
            )}
        </section>
    );
};

export { SemiFinishedCreateModal };
